/**
 * List Card Info Section - Card details for list context
 *
 * Shows quantity, priority, and notes for a card in a list.
 * Includes controls for adjusting quantity and priority.
 */
import clsx from 'clsx';
import type { ReactElement } from 'react';
import { IoDocument } from 'react-icons/io5';

import InlineEditText from '@/components/InlineEditText/InlineEditText';
import QuantityStepper from '@/components/QuantityStepper/QuantityStepper';
import useUserSettings from '@/features/settings/hooks/api';
import { formatPrice } from '@/lib/price';
import { PRIORITY_LABELS, type EnrichedListCard, type ListPriority } from '@/types/lists';

import { LIST_PRIORITY_BADGE, LIST_PRIORITY_ORDER } from '../lib/listPriority';

export interface ListCardInfoSectionProps {
  card: EnrichedListCard;
  onQuantityChange: (quantity: number) => void;
  onPriorityChange: (priority: ListPriority) => void;
  onNotesChange?: (notes: string | null) => void;
  isUpdating?: boolean;
}

export default function ListCardInfoSection({
  card,
  onQuantityChange,
  onPriorityChange,
  onNotesChange,
  isUpdating = false,
}: ListCardInfoSectionProps): ReactElement {
  const { settings } = useUserSettings();

  return (
    <section className="px-6 pb-6">
      <div className="bg-slate/50 border-storm rounded-xl border p-5">
        {/* Section Header */}
        <h3 className="text-ash mb-4 text-xs font-medium tracking-widest uppercase">List Entry</h3>

        {/* Quantity + Priority Row */}
        <div className="flex flex-wrap items-center gap-4">
          {/* Quantity Controls */}
          <QuantityStepper
            size="md"
            value={card.quantity}
            onIncrease={() => onQuantityChange(card.quantity + 1)}
            onDecrease={() => onQuantityChange(card.quantity - 1)}
            canDecrease={card.quantity > 1}
            disabled={isUpdating}
          />

          {/* Priority Selector */}
          <div className="flex items-center gap-2">
            <span className="text-ash text-xs">Priority:</span>
            <select
              value={card.priority}
              onChange={(e) => onPriorityChange(e.target.value as ListPriority)}
              disabled={isUpdating}
              className={clsx(
                'rounded-full px-3 py-1 text-xs font-medium ring-1 transition-colors',
                LIST_PRIORITY_BADGE[card.priority],
                'cursor-pointer bg-transparent focus:outline-none',
              )}
            >
              {LIST_PRIORITY_ORDER.map((priority) => (
                <option key={priority} value={priority} className="bg-obsidian text-pearl">
                  {PRIORITY_LABELS[priority]}
                </option>
              ))}
            </select>
          </div>

          {/* Ownership Status */}
          <div className="text-ash flex items-center gap-3 text-sm">
            <span
              className={clsx(
                'rounded-full px-3 py-1 text-xs font-medium',
                card.owned_quantity > 0 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-500/20 text-slate-400',
              )}
            >
              Owned: {card.owned_quantity}
            </span>
            {card.owned_other_editions > 0 && <span>+{card.owned_other_editions} other editions</span>}
            <span>Available: {card.available_quantity}</span>
          </div>
        </div>

        {/* Notes */}
        <div className="mt-4 flex items-start gap-2 text-sm">
          <IoDocument className="text-ash mt-1 h-4 w-4 shrink-0" />
          {onNotesChange ? (
            <InlineEditText
              value={card.notes}
              onCommit={onNotesChange}
              placeholder="Add a note…"
              ariaLabel={`Notes for ${card.name}`}
              disabled={isUpdating}
            />
          ) : (
            <span className="text-silver italic">{card.notes}</span>
          )}
        </div>

        {/* Price Info */}
        {card.unit_price != null && (
          <div className="mt-4 flex items-center gap-4 text-sm">
            <span className="text-ash">Price each:</span>
            <span className="text-silver font-mono">{formatPrice(card.unit_price, settings.currency)}</span>
            {card.quantity > 1 && card.total_price != null && (
              <span className="text-ash">
                Total: <span className="text-silver font-mono">{formatPrice(card.total_price, settings.currency)}</span>
              </span>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
